import React, { useState } from 'react';
import { IConfig } from '../../Interfaces/IConfig';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, Theme, useTheme } from '@mui/material/styles';
import { Logout, PrivacyTipOutlined } from '@mui/icons-material';
import md5 from 'md5';

import { PasswordResetNew } from '../core/PasswordResetNew';
import { DialogTopBar } from '../core/DialogTopBar';
import { sanitizeHtml } from '../../helpers/htmlSanitize';
import { customFetcher, useFetch } from '../../hooks/useFetch';
import { IContact } from '../../Interfaces/IContact';
import { useAcademicTitles, useGenders } from '../../hooks/data';
import { Loader } from '../generic/Loader';

interface IProps {
  configObject: IConfig;
}

export const LoginData: React.FC<IProps> = ({ configObject }) => {
  const theme = useTheme();
  const { genders, isLoadingGenders } = useGenders();
  const { academicTitles, isLoadingAcademicTitles } = useAcademicTitles();
  const [clientEdit, setClientEdit, wasSuccessfully] =
    useFetch<IContact>('/contact');

  const [newPassword, setNewPassword] = useState<string>('');
  const [passwordIsValid, setPasswordIsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wasSuccessfullySaved, setWasSuccessfullySaved] = useState(true);
  const [isDsgvoOpen, setIsDsgvoOpen] = useState(false);
  const [isTermsAndConditionsChecked, setIsTermsAndConditionsChecked] =
    useState(false);

  const handleAfterSave = () => {
    localStorage.setItem('passwordResetRequired', 'false');
    window.location.reload();
  };

  const handleSubmitPasswordChange = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (passwordIsValid) {
      setIsSubmitting(true);
      const uplaodObject = {
        ...clientEdit,
        Password: md5(newPassword),
      } as IContact;

      if ('allowDataChange' in uplaodObject) {
        uplaodObject.allowDataChange = false;
      }
      customFetcher({ endpoint: '/contact', method: 'PUT', body: uplaodObject })
        .then(() => { handleAfterSave(); setWasSuccessfullySaved(true); })
        .catch(() => setWasSuccessfullySaved(false))
        .finally(() => setIsSubmitting(false));
    }
  };

  if (
    !clientEdit ||
    isLoadingGenders ||
    isLoadingAcademicTitles
  ) {
    return <Loader />;
  }

  if (
    !wasSuccessfully ||
    !wasSuccessfullySaved ||
    !genders ||
    !academicTitles
  ) {
    return <Alert severity='error'>Es ist ein Fehler aufgetreten!</Alert>;
  }

  const isPerson = clientEdit.idBusinessForm === 1;
  const submitDisabled =
    !isTermsAndConditionsChecked ||
    !passwordIsValid ||
    clientEdit.LastName === '' ||
    (isPerson && clientEdit.FirstName === '');

  return (
    <>
      <Dialog
        open={isDsgvoOpen}
        onClose={() => setIsDsgvoOpen(false)}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: theme => ({
            borderRadius: 3,
            boxShadow: `0 24px 64px ${alpha(theme.palette.primary.main, 0.18)}`,
          }),
        }}
      >
        <DialogTopBar
          icon={<PrivacyTipOutlined />}
          title='Datenschutzerklärung'
          onClose={() => setIsDsgvoOpen(false)}
        />
        <DialogContent sx={{ pt: 2 }}>
          <Box
            sx={{
              fontSize: '0.9rem',
              lineHeight: 1.55,
              '& p': { my: 1 },
              '& h1, & h2, & h3': { mt: 2 },
            }}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(configObject.gdpr_html),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setIsDsgvoOpen(false)} variant='contained'>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: { xs: 4, md: 6 },
        }}
      >
        <Grid
          container
          spacing={{ xs: 3, md: 5 }}
          sx={{ maxWidth: 1180, alignItems: 'stretch' }}
        >
          {/* Hero / Branding */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                height: '100%',
                borderRadius: 6,
                p: { xs: 3, md: 5 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha('#ffffff', 0.88)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
              }}
            >
              <Box>
                <Box
                  component='img'
                  alt={`${configObject.company_name} Logo`}
                  src={`data:${configObject.logo_mime};base64,${configObject.logo}`}
                  sx={{
                    width: 'auto',
                    maxWidth: 240,
                    maxHeight: 88,
                    objectFit: 'contain',
                    mb: 4,
                  }}
                />
                <Typography variant='overline' sx={{ color: 'text.secondary' }}>
                  Erstanmeldung
                </Typography>
                <Typography variant='h4' sx={{ mb: 2, lineHeight: 1.2 }}>
                  Willkommen in Ihrem Mandantenportal.
                </Typography>
                <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                  Bitte überprüfen Sie kurz Ihre persönlichen Daten und vergeben
                  Sie ein neues, sicheres Passwort. Anschließend steht Ihnen
                  Ihr Portal in vollem Umfang zur Verfügung.
                </Typography>
              </Box>

              <Stack spacing={1.25} sx={{ mt: 4 }}>
                <Typography variant='body2' sx={{ fontWeight: 700 }}>
                  Schritte
                </Typography>
                <Stack direction='row' spacing={1.25} alignItems='center'>
                  <Box sx={stepDot(theme, 1)}>1</Box>
                  <Typography variant='body2' color='text.secondary'>
                    Persönliche Daten prüfen
                  </Typography>
                </Stack>
                <Stack direction='row' spacing={1.25} alignItems='center'>
                  <Box sx={stepDot(theme, 2)}>2</Box>
                  <Typography variant='body2' color='text.secondary'>
                    Sicheres Passwort vergeben
                  </Typography>
                </Stack>
                <Stack direction='row' spacing={1.25} alignItems='center'>
                  <Box sx={stepDot(theme, 3)}>3</Box>
                  <Typography variant='body2' color='text.secondary'>
                    Datenschutzerklärung bestätigen
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Grid>

          {/* Formular */}
          <Grid item xs={12} md={7}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'stretch',
              }}
            >
              <CardContent sx={{ width: '100%', p: { xs: 3, md: 5 } }}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  sx={{ mb: 3 }}
                >
                  <Box>
                    <Typography variant='h5' sx={{ fontWeight: 700 }}>
                      Stammdaten verifizieren
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Alle Felder mit * sind erforderlich.
                    </Typography>
                  </Box>
                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={<Logout />}
                    onClick={() => {
                      localStorage.removeItem('SessionToken');
                      window.location.reload();
                    }}
                  >
                    Abmelden
                  </Button>
                </Stack>

                <form onSubmit={handleSubmitPasswordChange} autoComplete='on'>
                  {/* Stammdaten */}
                  <SectionHeader index={1} title='Persönliche Daten' />
                  <Grid container spacing={2}>
                    {isPerson && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label='Anrede'
                            size='small'
                            fullWidth
                            select
                            value={clientEdit.idGender}
                            onChange={event =>
                              setClientEdit({
                                ...clientEdit,
                                idGender: Number(event.target.value),
                              })
                            }
                            required
                          >
                            {genders.map(gender => (
                              <MenuItem
                                key={`idGender-${gender.idGender}`}
                                value={gender.idGender}
                              >
                                {gender.Gender}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label='Titel'
                            size='small'
                            fullWidth
                            select
                            value={clientEdit.idAcademicTitle}
                            onChange={event =>
                              setClientEdit({
                                ...clientEdit,
                                idAcademicTitle: Number(event.target.value),
                              })
                            }
                            required
                          >
                            {academicTitles.map(academicTitle => (
                              <MenuItem
                                key={`idAcademicTitle-${academicTitle.idAcademicTitle}`}
                                value={academicTitle.idAcademicTitle}
                              >
                                {academicTitle.AcademicTitle}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label='Vorname'
                            size='small'
                            fullWidth
                            value={clientEdit.FirstName}
                            onChange={event =>
                              setClientEdit({
                                ...clientEdit,
                                FirstName: event.target.value,
                              })
                            }
                            error={clientEdit.FirstName === ''}
                            required
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12} sm={isPerson ? 6 : 12}>
                      <TextField
                        label={isPerson ? 'Nachname' : 'Gesellschaftsname'}
                        size='small'
                        fullWidth
                        value={clientEdit.LastName}
                        onChange={event =>
                          setClientEdit({
                            ...clientEdit,
                            LastName: event.target.value,
                          })
                        }
                        error={clientEdit.LastName === ''}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label='Mobiltelefon'
                        size='small'
                        fullWidth
                        value={clientEdit.PhoneNumber ?? ''}
                        onChange={event =>
                          setClientEdit({
                            ...clientEdit,
                            PhoneNumber:
                              event.target.value === ''
                                ? null
                                : event.target.value,
                          })
                        }
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3.5 }} />

                  {/* Passwort */}
                  <SectionHeader index={2} title='Neues Passwort vergeben' />
                  <Box>
                    <PasswordResetNew
                      passwordIsValid
                      setPasswordIsValid={setPasswordIsValid}
                      setNewPassword={setNewPassword}
                    />
                  </Box>

                  <Divider sx={{ my: 3.5 }} />

                  {/* DSGVO */}
                  <SectionHeader index={3} title='Datenschutz' />
                  <Box
                    sx={theme => ({
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    })}
                  >
                    <FormControlLabel
                      label={
                        <Typography variant='body2' component='span'>
                          Ich bin mit der Verarbeitung meiner Daten zur
                          Interaktion und zum Datenaustausch mit dem
                          Mandantenportal von{' '}
                          <Box
                            component='span'
                            sx={{ fontStyle: 'italic', fontWeight: 600 }}
                          >
                            {configObject.company_name}
                          </Box>{' '}
                          einverstanden. Die{' '}
                          <Box
                            component='span'
                            onClick={() => setIsDsgvoOpen(true)}
                            sx={{
                              color: 'primary.main',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              fontWeight: 600,
                            }}
                          >
                            Datenschutzerklärung
                          </Box>{' '}
                          habe ich zur Kenntnis genommen.
                        </Typography>
                      }
                      control={
                        <Checkbox
                          checked={isTermsAndConditionsChecked}
                          onChange={event =>
                            setIsTermsAndConditionsChecked(event.target.checked)
                          }
                          required
                          sx={{ alignSelf: 'flex-start', mt: -0.5 }}
                        />
                      }
                      sx={{ alignItems: 'flex-start', m: 0 }}
                    />
                  </Box>

                  <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant='contained'
                      color='primary'
                      type='submit'
                      size='large'
                      disabled={submitDisabled || isSubmitting}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress size={18} color='inherit' />
                        ) : undefined
                      }
                    >
                      {/* Eigenes span: Eine Browser-Übersetzung ersetzt lose
                          Textknoten, dann scheitert das Einfügen des
                          Lade-Symbols davor (#1337). */}
                      <span>
                        {isSubmitting
                          ? 'Wird gespeichert …'
                          : 'Stammdaten bestätigen & anmelden'}
                      </span>
                    </Button>
                  </Box>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

const stepDot = (theme: Theme, _index: number) => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.75rem',
  fontWeight: 700,
  backgroundColor: alpha(theme.palette.primary.main, 0.12),
  color: theme.palette.primary.main,
});

const SectionHeader: React.FC<{ index: number; title: string }> = ({
  index,
  title,
}) => {
  const theme = useTheme();
  return (
    <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 2 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.8rem',
          fontWeight: 700,
          backgroundColor: alpha(theme.palette.primary.main, 0.12),
          color: theme.palette.primary.main,
        }}
      >
        {index}
      </Box>
      <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
    </Stack>
  );
};
